import { useMemo, useState } from 'react';
import { Table, Input, Button, Spin, message, Tag, Modal, Tooltip, Form, InputNumber, Typography, Select, DatePicker, Divider, Avatar } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  LockOutlined,
  SearchOutlined,
  UnlockOutlined,
  EyeOutlined,
  EditOutlined,
  WalletOutlined,
  SyncOutlined,
  IdcardOutlined,
  CopyOutlined,
  ReloadOutlined,
  FileTextOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useBlockUnblockUser, useFetchUsers, useEditCustomer, useAddCustomerCredit, useResetCustomerPin, useGetCustomerPin, useDeleteCustomer } from './Hooks';
import { useFetchFactorySelect } from '../factories/Hooks';
import { useCustomerPeriodReport } from '../report/Hook/Index';
import useDebounce from '../../utils/debounce';
import CustomerDrawer from '../../components/CreateDrawer';
import CustomerDrawerView from '../../components/CustomerDrawerView';
import EditCustomerDrawer from '../../components/EditCustomerDrawer';
import AddRFIDModalDirectlyUser from '../../components/Modals/AddRFIDModalDirectlyUser';
import FactoryTouBoard from '../tou-pricing/FactoryTouBoard';
import { useIsEditAllowedWithOperator, formatCurrency, formatDisplayPhone } from '../../utils/utils';
import { useOperatorStore } from '../../store/useOperatorStore';
import { useAuthStore } from '../../store/useAuthStore';
import WarningMessage from '../../components/WarningMessage';
import { getErrorMessage } from '../../utils/errorHandler';

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const CustomersListPage = () => {
  const { t } = useTranslation('users');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const showWallet = import.meta.env.VITE_SHOW_WALLET === 'true';
  const isEditAllowed = useIsEditAllowedWithOperator();
  const { operatorId } = useOperatorStore();
  const { user } = useAuthStore();
  const canGenerateReport =
    !!operatorId && user?.role === 'ADMIN';
  const [searchQuery, setSearchQuery] = useState('');
  const [factoryFilter, setFactoryFilter] = useState();
  const [consentFilter, setConsentFilter] = useState();
  const debouncedSearch = useDebounce(searchQuery, 400);
  const { data: factories = [] } = useFetchFactorySelect({ operatorId, kind: 'PARENT' });

  const [page, setPage] = useState(1);
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [customerToBlock, setCustomerToBlock] = useState(null);
  const [isUnblockModalOpen, setIsUnblockModalOpen] = useState(false);
  const [customerToUnblock, setCustomerToUnblock] = useState(null);
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);
  const [customerToTopUp, setCustomerToTopUp] = useState(null);
  const [isAddRFIDModalOpen, setIsAddRFIDModalOpen] = useState(false);
  const [customerForRFID, setCustomerForRFID] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [customerForReport, setCustomerForReport] = useState(null);
  const [reportDateRange, setReportDateRange] = useState(null);
  const [reportMonthGates, setReportMonthGates] = useState({});
  const [form] = Form.useForm();
  const [reportForm] = Form.useForm();
  const watchedReportRange = Form.useWatch('dateRange', reportForm);
  const reportMonths = useMemo(() => {
    const start = watchedReportRange?.[0];
    const end = watchedReportRange?.[1];
    if (!start || !end) return [];
    const months = [];
    let cursor = start.startOf('month');
    const last = end.startOf('month');
    while ((cursor.isBefore(last) || cursor.isSame(last, 'month')) && months.length < 3) {
      months.push({
        year: cursor.year(),
        month: cursor.month() + 1,
        key: cursor.format('YYYY-MM'),
      });
      cursor = cursor.add(1, 'month');
    }
    return months;
  }, [watchedReportRange]);
  const reportRatesReady =
    reportMonths.length > 0 &&
    reportMonths.every(
      (item) =>
        reportMonthGates[item.key]?.ready &&
        !reportMonthGates[item.key]?.dirty &&
        !reportMonthGates[item.key]?.loading,
    );

  const pageSize = 10;
  const {
    data: users,
    isLoading,
    isFetching,
    refetch,
  } = useFetchUsers({
    operatorId,
    search: debouncedSearch,
    offset: page,
    limit: pageSize,
    sortBy: '',
    sortOrder: 'asc',
    factoryId: factoryFilter,
    consentStatus: consentFilter,
  });
  const { mutate: toggleBlock } = useBlockUnblockUser({
    onSuccess: (data) => {
      queryClient.invalidateQueries(['users']);
      setLoadingUserId('');
      if (data?.customer?.isBlocked) {
        message.success(t('UserBlocked'));
      } else {
        message.success(t('UserUnblocked'));
      }
    },
    onError: (error) => {
      message.error(getErrorMessage(error, t('ActionFailed')));
      setLoadingUserId('');
    },
  });

  const { mutate: editCustomer, isPending } = useEditCustomer(selectedCustomer?._id, {
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setIsEditDrawerOpen(false);
      refetch();
      message.success(t('UserUpdated'));
    },
    onError: (error) => {
      message.error(getErrorMessage(error, t('ActionFailed')));
    },
  });

  const { mutateAsync: deleteCustomer } = useDeleteCustomer({
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      message.success(t('UserDeleted', 'Employee deleted'));
    },
    onError: (error) => {
      message.error(getErrorMessage(error, t('ActionFailed')));
    },
  });

  const { mutate: addCredit, isPending: isAddingCredit } = useAddCustomerCredit({
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setIsTopUpModalOpen(false);
      form.resetFields();
      message.success(t('CreditAddedSuccessfully'));
    },
    onError: (error) => {
      message.error(getErrorMessage(error, t('ActionFailed')));
    },
  });

  const [resetPinModalOpen, setResetPinModalOpen] = useState(false);
  const [customerToResetPin, setCustomerToResetPin] = useState(null);
  const [showPinModalOpen, setShowPinModalOpen] = useState(false);
  const [newlyGeneratedPin, setNewlyGeneratedPin] = useState(null);

  const { mutate: getPin } = useGetCustomerPin({
    onSuccess: (data) => {
      setNewlyGeneratedPin(data?.pin);
      setShowPinModalOpen(true);
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || t('FailedToGetPin'));
    }
  });

  const { mutate: resetPin, isPending: isResettingPin } = useResetCustomerPin({
    onSuccess: () => {
      message.success(t('PinResetSuccess'));
      setResetPinModalOpen(false);
      if (customerToResetPin?._id) {
        getPin({ id: customerToResetPin._id });
      }
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || t('PinResetFailed'));
    }
  });

  const { mutate: generatePeriodReport, isPending: isGeneratingReport } =
    useCustomerPeriodReport({
      onSuccess: (data) => {
        const reportMonthKey =
          data?.data?.reportMonthKey ||
          (reportDateRange?.[0]
            ? reportDateRange[0].format('YYYY-MM')
            : dayjs().format('YYYY-MM'));
        const customerId = customerForReport?._id;
        message.success(t('GenerateReportSuccess'));
        setIsReportModalOpen(false);
        setCustomerForReport(null);
        setReportDateRange(null);
        reportForm.resetFields();
        navigate('/dashboard/reports', {
          state: {
            activeMainTab: 'reports',
            activeReportTab: 'CUSTOMER',
            selectedMonth: reportMonthKey,
            reportCustomerId: customerId,
          },
        });
      },
      onError: (error) => {
        message.error(getErrorMessage(error, t('GenerateReportFailed')));
      },
    });

  const handleResetPin = (customer) => {
    setCustomerToResetPin(customer);
    setResetPinModalOpen(true);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (page !== 1) {
      setPage(1);
    }
  };

  const totalUsers = users?.data?.totalCount ?? 0;

  const handleToggleBlock = (userId, isBlocked) => {
    if (isBlocked) {
      setCustomerToUnblock(userId);
      setIsUnblockModalOpen(true);
    } else {
      setCustomerToBlock(userId);
      setIsBlockModalOpen(true);
    }
  };

  const confirmBlockCustomer = () => {
    setLoadingUserId(customerToBlock);
    toggleBlock({ id: customerToBlock, status: true, operatorId });
    setIsBlockModalOpen(false);
  };

  const confirmUnblockCustomer = () => {
    setLoadingUserId(customerToUnblock);
    toggleBlock({ id: customerToUnblock, status: false, operatorId });
    setIsUnblockModalOpen(false);
  };

  const openCustomerViewDrawer = (customer) => {
    setSelectedCustomer(customer);
    setIsViewDrawerOpen(true);
  };

  const openCustomerEditDrawer = (customer) => {
    setSelectedCustomer(customer);
    setIsEditDrawerOpen(true);
  };

  const handleTopUp = (customer) => {
    setCustomerToTopUp(customer);
    setIsTopUpModalOpen(true);
  };

  const confirmTopUp = async () => {
    try {
      const values = await form.validateFields();
      addCredit({
        customerId: customerToTopUp._id,
        amount: values.amount.toString(),
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleAddRFID = (customer) => {
    setCustomerForRFID(customer);
    setIsAddRFIDModalOpen(true);
  };

  const handleOpenReportModal = (customer) => {
    setCustomerForReport(customer);
    setReportDateRange(null);
    setReportMonthGates({});
    reportForm.resetFields();
    setIsReportModalOpen(true);
  };

  const confirmGenerateReport = async () => {
    try {
      const values = await reportForm.validateFields();
      const range = values.dateRange;
      if (!range?.[0] || !range?.[1] || !customerForReport?._id) return;
      const now = dayjs();
      const earliest = now.subtract(2, 'month').startOf('month');
      const latest = now.endOf('day');
      if (
        range[0].isBefore(earliest, 'day') ||
        range[1].isAfter(latest, 'day') ||
        range[1].diff(range[0], 'month', true) > 3
      ) {
        message.error(t('DateRangeLastThreeMonthsOnly'));
        return;
      }
      if (!reportRatesReady) {
        message.error(t('ReportTouIncomplete'));
        return;
      }
      setReportDateRange(range);
      generatePeriodReport({
        customerId: customerForReport._id,
        fromDate: range[0].format('YYYY-MM-DD'),
        toDate: range[1].format('YYYY-MM-DD'),
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const columns = [
    {
      title: '',
      key: 'photo',
      width: 56,
      render: (_, record) => (
        <Avatar src={record.profilePhoto || undefined} alt={record.fname || ''}>
          {(record.fname || '?').slice(0, 1)}
        </Avatar>
      ),
    },
    {
      title: t('Name'),
      dataIndex: 'name',
      align: 'center',
      key: 'name',
      render: (_, record) => (
        <Button type="link" onClick={() => openCustomerViewDrawer(record)}>
          {`${record.fname || '-'} ${record.lname || '-'}`}
        </Button>
      ),
    },

    {
      title: t('PhoneNumber'),
      dataIndex: 'phoneNumber',
      align: 'center',
      key: 'phoneNumber',
      width: 220,
      render: (_, record) => formatDisplayPhone(record.countryCode, record.phoneNumber),
    },
    {
      title: t('EmployeeId'),
      dataIndex: 'employeeId',
      key: 'employeeId',
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: t('Position'),
      dataIndex: 'position',
      key: 'position',
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: t('ConsentStatus'),
      dataIndex: 'consentStatus',
      key: 'consentStatus',
      width: 220,
      ellipsis: false,
      render: (value) => {
        if (value === 'PENDING') {
          return <Tag color="orange">{t('ConsentPending')}</Tag>;
        }
        if (value === 'CONSENTED') {
          return <Tag color="green">{t('ConsentSigned')}</Tag>;
        }
        return <Tag>{t('ConsentNotRequired')}</Tag>;
      },
    },
    {
      title: t('FactoryNumber'),
      dataIndex: 'factoryNumber',
      key: 'factoryNumber',
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: t('TagId'),
      dataIndex: 'tagId',
      key: 'tagId',
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: t('StartCharge'),
      dataIndex: 'startChargeEnabled',
      key: 'startChargeEnabled',
      align: 'center',
      render: (value) => (
        <Tag color={value === true ? 'blue' : 'orange'} variant="outlined">
          {value === true ? t('StartChargeEnabledLabel') : t('StartChargeDisabled')}
        </Tag>
      ),
    },
    {
      title: t('FreeTierColumn'),
      dataIndex: 'freeTierEnabled',
      key: 'freeTierEnabled',
      align: 'center',
      responsive: ['lg'],
      render: (_, record) =>
        record.freeTierEnabled ? (
          <Tag color="cyan" variant="outlined">
            {record.freeTierUnit === 'CURRENCY'
              ? formatCurrency(Number(record.freeTierMonthlyThreshold || 0))
              : `${Number(record.freeTierMonthlyThreshold || 0)} kWh`}
          </Tag>
        ) : (
          <Tag color="default" variant="outlined">
            {t('FreeTierOff')}
          </Tag>
        ),
    },
    {
      title: t('Status'),
      dataIndex: 'isBlocked',
      key: 'status',
      align: 'center',
      width: 100,
      render: (value) => (
        <Tag color={value ? 'red' : 'green'} variant="outlined">
          {value ? t('Blocked') : t('Active')}
        </Tag>
      ),
    },
    {
      title: t('Action'),
      key: 'action',
      align: 'center',
      width: 360,
      fixed: 'right',
      render: (_, record) => (
        <div className="flex gap-2 justify-center flex-nowrap">
          <div className="">
            <Tooltip title={t('View')}>
              <Button
                icon={<EyeOutlined />}
                type="default"
                onClick={() => openCustomerViewDrawer(record)}
              />
            </Tooltip>
          </div>
          {isEditAllowed && (
            <div className="">
              <Tooltip title={t('EditCustomer')}>
                <Button
                  icon={<EditOutlined />}
                  type="default"
                  onClick={() => openCustomerEditDrawer(record)}
                />
              </Tooltip>
            </div>
          )}
          {isEditAllowed && (
            <div className="">
              <Tooltip title={t('ResetPin')}>
                <Button
                  icon={<SyncOutlined />}
                  type="default"
                  onClick={() => handleResetPin(record)}
                />
              </Tooltip>
            </div>
          )}
          {isEditAllowed && (
            <div className="">
              <Tooltip title={t('AddRFID')}>
                <Button
                  icon={<IdcardOutlined />}
                  type="default"
                  onClick={() => handleAddRFID(record)}
                />
              </Tooltip>
            </div>
          )}
          {showWallet && isEditAllowed && (
            <div className="">
              <Tooltip title={t('topUpBalance')}>
                <Button
                  icon={<WalletOutlined />}
                  type="default"
                  onClick={() => handleTopUp(record)}
                />
              </Tooltip>
            </div>
          )}
          {canGenerateReport && (
            <div className="">
              <Tooltip title={t('GenerateReport')}>
                <Button
                  icon={<FileTextOutlined />}
                  type="default"
                  onClick={() => handleOpenReportModal(record)}
                />
              </Tooltip>
            </div>
          )}
          {isEditAllowed && (
            <div className="">
              {record.isBlocked ? (
                <Tooltip title={t('Unblock')}>
                  <Button
                    icon={<UnlockOutlined />}
                    loading={loadingUserId === record._id}
                    onClick={() => handleToggleBlock(record._id, true)}
                  />
                </Tooltip>
              ) : (
                <Tooltip title={t('Block')}>
                  <Button
                    icon={<LockOutlined />}
                    loading={loadingUserId === record._id}
                    onClick={() => handleToggleBlock(record._id, false)}
                  />
                </Tooltip>
              )}
            </div>
          )}
          {isEditAllowed && (
            <div className="">
              <Tooltip title={t('Delete')}>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    Modal.confirm({
                      title: t('DeleteEmployee', 'Delete employee?'),
                      content: t('DeleteEmployeeConfirm', 'The employee will be hidden. Charging history, invoices, RFID cards, and consent records stay.'),
                      okText: t('Delete'),
                      okButtonProps: { danger: true },
                      onOk: () => deleteCustomer({ id: record._id }).catch(() => {}),
                    });
                  }}
                />
              </Tooltip>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {!operatorId ? (
        <WarningMessage />
      ) : (
        <>
          <div className="flex justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Input
                allowClear
                placeholder={t('SearchPlaceholder')}
                prefix={<SearchOutlined />}
                style={{ width: '300px' }}
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <Select
                allowClear
                placeholder={t('FilterByConsent')}
                style={{ width: 200 }}
                value={consentFilter}
                onChange={(value) => {
                  setConsentFilter(value);
                  if (page !== 1) setPage(1);
                }}
                options={[
                  { value: 'PENDING', label: t('ConsentPending') },
                  { value: 'CONSENTED', label: t('ConsentSigned') },
                  { value: 'NOT_REQUIRED', label: t('ConsentNotRequired') },
                ]}
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('FilterByFactory')}
                style={{ width: 220 }}
                value={factoryFilter}
                onChange={(value) => {
                  setFactoryFilter(value);
                  if (page !== 1) setPage(1);
                }}
                options={factories.map((item) => ({
                  value: item._id,
                  label: item.name ? `${item.number} — ${item.name}` : item.number,
                }))}
              />
              <Tooltip title={t('Refresh')}>
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  loading={isFetching}
                />
              </Tooltip>
            </div>
            {isEditAllowed && (
              <Button type="primary" onClick={() => setIsDrawerOpen(true)}>
                {t('AddCustomer')}
              </Button>
            )}
          </div>
          {isLoading ? (
            <div className="flex justify-center">
              <Spin tip={t('Loading')} />
            </div>
          ) : (
            <Table
              variant="outlined"
              columns={columns}
              dataSource={users?.data?.paginatedData || []}
              loading={isFetching}
              size="small"
              tableLayout="fixed"
              scroll={{ x: 'max-content', y: 60 * 8 }}
              pagination={{
                current: page,
                pageSize,
                size: 'default',
                showSizeChanger: false,
                total: totalUsers,
                onChange: (p) => setPage(p),
                position: ['bottomCenter'],
              }}
              rowKey={(record) => record._id}
            />
          )}
      {/* Modal for Blocking Resident */}
      <Modal
        confirmLoading={loadingUserId === customerToBlock}
        title={<Title level={4} className="mb-0!">{t('BlockCustomerTitle')}</Title>}
        open={isBlockModalOpen}
        onCancel={() => setIsBlockModalOpen(false)}
        onOk={confirmBlockCustomer}
      >
        <p>{t('BlockCustomerMessage')}</p>
      </Modal>
      {/* Modal for Unblocking Resident */}
      <Modal
        confirmLoading={loadingUserId === customerToUnblock}
        title={<Title level={4} className="mb-0!">{t('UnblockCustomerTitle')}</Title>}
        open={isUnblockModalOpen}
        onCancel={() => setIsUnblockModalOpen(false)}
        onOk={confirmUnblockCustomer}
      >
        <p>{t('UnblockCustomerMessage')}</p>
      </Modal>
      {/* TopUp Modal */}
      <Modal
        title={<Title level={4} className="mb-0!">{t('TopUpBalance')}</Title>}
        open={isTopUpModalOpen}
        onCancel={() => {
          setIsTopUpModalOpen(false);
          form.resetFields();
        }}
        onOk={confirmTopUp}
        okButtonProps={{
          disabled: isAddingCredit,
          loading: isAddingCredit,
        }}
        confirmLoading={isAddingCredit}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="amount"
            label={t('Amount')}
            rules={[
              { required: true, message: t('PleaseEnterAmount') },
              { type: 'number', min: 0, message: t('AmountMustBePositive') }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t('EnterAmount')}
              prefix="₹"
            />
          </Form.Item>
        </Form>
      </Modal>
      {/* Generate period report modal */}
      <Modal
        title={<Title level={4} className="mb-0!">{t('GenerateReportTitle')}</Title>}
        open={isReportModalOpen}
        onCancel={() => {
          setIsReportModalOpen(false);
          setCustomerForReport(null);
          setReportDateRange(null);
          setReportMonthGates({});
          reportForm.resetFields();
        }}
        onOk={confirmGenerateReport}
        okText={t('GenerateReport')}
        okButtonProps={{
          disabled: isGeneratingReport || !reportRatesReady,
          loading: isGeneratingReport,
        }}
        confirmLoading={isGeneratingReport}
        destroyOnClose
        width={960}
      >
        <Paragraph type="secondary" className="mb-3!">
          {customerForReport
            ? `${customerForReport.fname || ''} ${customerForReport.lname || ''}`.trim()
            : ''}
        </Paragraph>
        <Paragraph type="secondary" className="mb-3!">
          {t('GenerateReportSubtitle')}
        </Paragraph>
        <Form form={reportForm} layout="vertical">
          <Form.Item
            name="dateRange"
            label={t('DateRange')}
            rules={[{ required: true, message: t('DateRangeRequired') }]}
          >
            <RangePicker
              className="w-full"
              format="YYYY-MM-DD"
              disabledDate={(current) => {
                if (!current) return false;
                const now = dayjs();
                const earliest = now.subtract(2, 'month').startOf('month');
                const latest = now.endOf('day');
                return (
                  current.isBefore(earliest, 'day') ||
                  current.isAfter(latest, 'day')
                );
              }}
              onChange={() => setReportMonthGates({})}
            />
          </Form.Item>
        </Form>

        {reportMonths.length && operatorId
          ? reportMonths.map((item) => (
              <div key={item.key} className="mt-2">
                <Divider orientation="left" className="mt-2! mb-3!">
                  <Text strong>
                    {t('ReportTouRates')} · {item.key}
                  </Text>
                </Divider>
                <FactoryTouBoard
                  operatorId={operatorId}
                  year={item.year}
                  month={item.month}
                  onlyFactoryId={
                    customerForReport?.factoryId ||
                    (customerForReport?.factoryNumber ? null : 'fallback')
                  }
                  onlyFactoryNumber={
                    customerForReport?.factoryId ? '' : customerForReport?.factoryNumber || ''
                  }
                  reportMode
                  onReadyChange={(next) =>
                    setReportMonthGates((prev) => {
                      const current = prev[item.key];
                      if (
                        current?.ready === next.ready &&
                        current?.dirty === next.dirty &&
                        current?.loading === next.loading
                      ) {
                        return prev;
                      }
                      return { ...prev, [item.key]: next };
                    })
                  }
                />
              </div>
            ))
          : null}
      </Modal>
      {/* Reset PIN Modal */}
      <Modal
        title={<Title level={4} className="mb-0!">{t('ResetPinTitle')}</Title>}
        open={resetPinModalOpen}
        onCancel={() => {
          setResetPinModalOpen(false);
          setCustomerToResetPin(null);
        }}
        onOk={() => {
          resetPin({ id: customerToResetPin?._id });
        }}
        confirmLoading={isResettingPin}
      >
        <Typography.Text>{t('ResetPinConfirmation')}</Typography.Text>
      </Modal>
      {/* Add RFID Modal */}
      {isAddRFIDModalOpen && (
        <AddRFIDModalDirectlyUser
          open={isAddRFIDModalOpen}
          setClose={() => {
            setIsAddRFIDModalOpen(false);
            setCustomerForRFID(null);
          }}
          assignedTo={customerForRFID}
        />
      )}
      <Modal
        title={<Title level={4} className="mb-0!">{t('ClientPin')}</Title>}
        open={showPinModalOpen}
        centered
        onCancel={() => {
          setShowPinModalOpen(false);
          setNewlyGeneratedPin(null);
          setCustomerToResetPin(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setShowPinModalOpen(false);
            setNewlyGeneratedPin(null);
            setCustomerToResetPin(null);
          }}>
            {t('Close')}
          </Button>
        ]}
      >
        <div className="flex flex-col items-center justify-center gap-6 py-4">
          <div className="text-center space-y-1">
            <Typography.Text type="secondary">
              {t('NewPinGeneratedFor')}
            </Typography.Text>
            <div className="font-semibold text-lg">
              {customerToResetPin?.fname} {customerToResetPin?.lname}
            </div>
          </div>

          <div className="flex items-center justify-center gap-3 w-full">
            <div className="bg-gray-50 h-12 min-w-[200px] px-4 rounded-lg border border-gray-200 flex items-center justify-center">
              <span className="font-mono text-xl font-bold text-slate-700 tracking-[0.2em]">
                {newlyGeneratedPin}
              </span>
              <Tooltip title={t('Copy')}>
                <CopyOutlined className="text-xl cursor-pointer ml-2 hover:text-primary" onClick={() => {
                  navigator.clipboard.writeText(newlyGeneratedPin);
                  message.success(t('PinCopied'));
                }} />
              </Tooltip>
            </div>

          </div>

          <Typography.Text type="secondary" className="text-center text-xs">
            {t('CopyHelp')}
          </Typography.Text>
        </div>
      </Modal>
      {/* Drawer for Adding a New Resident */}
      {isDrawerOpen && (
        <CustomerDrawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
      )}
      {/* Drawer for Viewing Resident Details & Transactions */}
      {isViewDrawerOpen && selectedCustomer && (
        <CustomerDrawerView
          open={isViewDrawerOpen}
          onClose={() => setIsViewDrawerOpen(false)}
          id={selectedCustomer?._id ?? selectedCustomer?.id}
          operatorId={operatorId}
        />
      )}
      {/* Drawer for Editing Resident Details */}
      {isEditDrawerOpen && selectedCustomer && (
        <EditCustomerDrawer
          open={isEditDrawerOpen}
          onClose={() => setIsEditDrawerOpen(false)}
          customer={selectedCustomer}
          isPending={isPending}
          onSave={editCustomer}
        />
      )}
        </>
      )}
    </div>
  );
};

export default CustomersListPage;
